import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ServiceService } from '../../service.service';

interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  options: any[];
  is_required: boolean;
  _id: string;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
  _id: string;
}

interface Answer {
  question_id: string;
  set_id: string;
  star_rating?: number;
  text_answer?: string;
  selected_option?: string;
  _id: string;
}

// Combined interface for both pending and completed assignments
interface Assignment {
  _id?: string;
  survey_id: string;
  survey_number: string;
  survey_title: string;
  survey_description: string;
  customer_id: string;
  customer_name: string;
  assigned_date?: string;
  conducted_date?: string;
  survey_status: string;
  questionnaire_sets: QuestionnaireSet[];
  answers?: Answer[];
  remarks?: string;
}

interface Employee {
  employee_id: string;
  employee_name: string;
  employee_code: string;
}

interface Statistics {
  total_completed: number;
  total_pending: number;
  total_assigned: number;
}

interface FeedbackResponse {
  success: boolean;
  message: string;
  employee: Employee;
  statistics: Statistics;
  completed_feedback: any[];
  pending_assignments: any[];
}

@Component({
  selector: 'app-feedback-intake',
  templateUrl: './feedback-intake.component.html',
  styleUrls: ['./feedback-intake.component.css']
})
export class FeedbackIntakeComponent implements OnInit {
  // Main data arrays
  allAssignments: Assignment[] = [];
  filteredAssignments: Assignment[] = [];
  
  // Employee and statistics
  employee: Employee | null = null;
  statistics: Statistics | null = null;
  
  // Filter properties
  searchText: string = '';
  selectedStatus: string = 'ALL';
  statusOptions: string[] = ['ALL', 'Pending', 'Completed'];
  
  // UI state
  isLoading: boolean = false;
  employeeId: string = '';

  constructor(
    private http: HttpClient,
    private service: ServiceService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Get employee_id from localStorage or query params
    this.employeeId = localStorage.getItem('admin_id') || '';
    
    this.route.queryParams.subscribe(params => {
      if (params['employee_id']) {
        this.employeeId = params['employee_id'];
      }
      
      if (this.employeeId) {
        this.loadFeedback();
      } else {
        this.service.showToast('error', 'Employee ID not found');
        this.router.navigate(['/admin']);
      }
    });
  }

  loadFeedback(): void {
    this.isLoading = true;
    
    this.service.viewCustomerSurveyFeedback(this.employeeId).subscribe({
      next: (response: FeedbackResponse) => {
        if (response.success) {
          // Assign data from API response
          this.employee = response.employee;
          this.statistics = response.statistics;
          
          // Combine both pending and completed assignments
          this.combineAssignments(
            response.pending_assignments || [],
            response.completed_feedback || []
          );
          
          // Apply any initial filters
          this.applyFilters();
          
          // this.service.showToast('success', 'Feedback assignments loaded successfully');
        } else {
          this.service.showToast('error', response.message || 'Failed to load feedback');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading feedback:', error);
        this.service.showToast('error', 'Failed to load feedback assignments');
        this.isLoading = false;
      }
    });
  }

  combineAssignments(pendingAssignments: any[], completedFeedback: any[]): void {
    // Map pending assignments
    const pending: Assignment[] = pendingAssignments.map(item => ({
      _id: item._id,
      survey_id: item.survey_id,
      survey_number: item.survey_number,
      survey_title: item.survey_title,
      survey_description: item.survey_description,
      customer_id: item.customer_id,
      customer_name: item.customer_name,
      assigned_date: item.assigned_date,
      survey_status: 'Pending',
      questionnaire_sets: item.questionnaire_sets || []
    }));

    // Map completed feedback
    const completed: Assignment[] = completedFeedback.map(item => ({
      survey_id: item.survey_id,
      survey_number: item.survey_number,
      survey_title: item.survey_title,
      survey_description: item.survey_description,
      customer_id: item.customer_id,
      customer_name: item.customer_name,
      conducted_date: item.conducted_date,
      survey_status: 'Completed',
      questionnaire_sets: item.questionnaire_sets || [],
      answers: item.answers || [],
      remarks: item.remarks || ''
    }));

    // Combine both arrays
    this.allAssignments = [...pending, ...completed];
    
    // Sort by date (most recent first)
    this.allAssignments.sort((a, b) => {
      const dateA = new Date(a.conducted_date || a.assigned_date || '').getTime();
      const dateB = new Date(b.conducted_date || b.assigned_date || '').getTime();
      return dateB - dateA;
    });
  }

  // Filter methods
  onSearch(): void {
    this.applyFilters();
  }

  onStatusChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.allAssignments];

    // Apply search filter
    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(assignment => 
        assignment.survey_title.toLowerCase().includes(search) ||
        assignment.survey_number.toLowerCase().includes(search) ||
        assignment.customer_name.toLowerCase().includes(search) ||
        assignment.survey_description.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (this.selectedStatus !== 'ALL') {
      filtered = filtered.filter(assignment => 
        assignment.survey_status === this.selectedStatus
      );
    }

    this.filteredAssignments = filtered;
  }

  // Navigation methods
  onProvideFeedback(assignment: Assignment): void {
    this.router.navigate(['/admin/customer-survey-menu/feedback-update', assignment.survey_id], {
      queryParams: {
        customer_id: assignment.customer_id,
        employee_id: this.employeeId,
        mode: 'provide'
      }
    });
  }

  onViewDetails(assignment: Assignment): void {
    this.router.navigate(['/admin/customer-survey-menu/feedback-detail', assignment.survey_id], {
      queryParams: {
        customer_id: assignment.customer_id,
        employee_id: this.employeeId,
        mode: 'view',
        status: assignment.survey_status
      }
    });
  }

  // Helper methods
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getDisplayDate(assignment: Assignment): string {
    // Show conducted_date for completed, assigned_date for pending
    if (assignment.survey_status === 'Completed') {
      return this.formatDate(assignment.conducted_date || '');
    } else {
      return this.formatDate(assignment.assigned_date || '');
    }
  }

  getDateLabel(assignment: Assignment): string {
    return assignment.survey_status === 'Completed' ? 'Completed On' : 'Assigned On';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'status-completed';
      case 'Pending':
        return 'status-pending';
      default:
        return 'status-default';
    }
  }

  getTotalQuestions(assignment: Assignment): number {
    if (!assignment.questionnaire_sets) return 0;
    return assignment.questionnaire_sets.reduce((total, set) => {
      return total + (set.questions?.length || 0);
    }, 0);
  }

  // Refresh data
  refreshData(): void {
    this.loadFeedback();
  }

  // Clear filters
  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = 'ALL';
    this.applyFilters();
  }
}